import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { AxiosError } from "axios";

export interface FieldErrors {
  [fieldName: string]: string[];
}

export interface ApiError {
  status: number;
  fieldErrors: FieldErrors;
  globalErrors: string[];
  errorCode?: string;
  timestamp: string;
  params?: Record<string, any>;
}

/**
 * Normalizes API errors from various sources into a consistent ApiError structure
 *
 * Handles:
 * 1. Server responses with structured errors (RTK Query and Axios)
 * 2. Network errors
 * 3. Serialized errors
 * 4. Generic JavaScript errors
 *
 * Prioritizes extracting error information from server responses first,
 * then falls back to other error types
 *
 * @param error - Raw error from any source (RTK Query, Axios, etc.)
 * @param defaultCode - Default error code if none is provided
 * @returns Normalized ApiError object
 */
export const normalizeApiError = (error: any, defaultCode = "API_ERROR"): ApiError => {
  const timestamp = new Date().toISOString();

  // Default error structure
  const result: ApiError = {
    status: 500,
    fieldErrors: {},
    globalErrors: [],
    timestamp,
    errorCode: defaultCode,
  };

  // ========================================================================
  // PHASE 1: Handle Server Responses (Structured Errors)
  // ========================================================================

  /**
   * Both RTK Query and Axios errors contain server response data, but in different structures:
   *
   * RTK Query (FetchBaseQueryError):
   *   error.status: HTTP status code (number or string)
   *   error.data: Response body (our structured ApiErrorResponse)
   *
   * Axios (AxiosError):
   *   error.response.status: HTTP status code
   *   error.response.data: Response body (our structured ApiErrorResponse)
   *
   * We first attempt to extract these values from either source
   */

  let serverErrorData: any = null;
  let httpStatus: number | undefined;

  // Case 1A: RTK Query error (FetchBaseQueryError)
  if (isFetchBaseQueryError(error)) {
    // RTK Query stores HTTP status directly on error.status (can be number or string)
    if (typeof error.status === "number") {
      httpStatus = error.status;
    } else if (typeof error.status === "string" && !isNaN(Number(error.status))) {
      httpStatus = Number(error.status);
    }

    // response body might be null/undefined for some errors (e.g. 401 without body)
    serverErrorData = error.data ?? null;
  }
  // Case 1B: Axios error (AxiosError)
  else if (isAxiosError(error)) {
    // Axios stores response details in error.response
    httpStatus = error.response?.status;
    serverErrorData = error.response?.data ?? null;
  }

  // If server returned no structured body, make sure we fallback to the HTTP status
  if (!serverErrorData && httpStatus) {
    result.status = httpStatus;
  }

  // Process server response data if available
  if (serverErrorData && typeof serverErrorData === "object") {
    /**
     * At this point, serverErrorData should be our backend's ApiErrorResponse structure:
     * {
     *   status: number,
     *   fieldErrors: { [field: string]: string[] },
     *   globalErrors: string[],
     *   errorCode?: string,
     *   params?: { [key: string]: any }
     * }
     */
    const data = serverErrorData;

    // Prefer status from response body, fallback to HTTP status
    result.status =
      typeof data.status === "number"
        ? data.status // Use status from response body
        : httpStatus || 500; // Fallback to HTTP status

    // Extract field errors (if provided)
    if (data.fieldErrors && typeof data.fieldErrors === "object") {
      result.fieldErrors = data.fieldErrors;
    }

    // Extract global errors (if provided)
    if (data.globalErrors && Array.isArray(data.globalErrors)) {
      result.globalErrors = data.globalErrors;
    }

    // Extract error code and parameters
    if (data.errorCode) result.errorCode = data.errorCode;
    if (data.params) result.params = data.params;
  }

  // ========================================================================
  // PHASE 2: Handle Non-Response Errors
  // ========================================================================

  // Only process if we didn't get any global errors from server response
  if (result.globalErrors.length === 0) {
    // Case 2A: RTK Query SerializedError (non-response errors)
    if (isSerializedError(error)) {
      result.globalErrors = [error.message || "UNKNOWN_ERROR"];
      if (error.code) result.errorCode = error.code;
    }
    // Case 2B: Generic JavaScript Error
    else if (error instanceof Error) {
      result.globalErrors = [error.message];
    }
    // Case 2C: String errors
    else if (typeof error === "string") {
      result.globalErrors = [error];
    }
  }

  // ========================================================================
  // PHASE 3: Final Fallback
  // ========================================================================

  // If we still have no error messages, provide a default
  if (result.globalErrors.length === 0 && Object.keys(result.fieldErrors).length === 0) {
    result.globalErrors = [getDefaultMessage(result.status)];
  }

  return result;
};

// ==========================================================================
// Type Guards
// ==========================================================================

/**
 * Type guard for RTK Query's FetchBaseQueryError
 * @param error - Error object to check
 * @returns True if error is a FetchBaseQueryError
 */
function isFetchBaseQueryError(error: any): error is FetchBaseQueryError {
  return error && typeof error === "object" && "status" in error;
}

/**
 * Type guard for RTK Query's SerializedError
 * @param error - Error object to check
 * @returns True if error is a SerializedError
 */
function isSerializedError(error: any): error is SerializedError {
  return error && typeof error === "object" && "message" in error && "name" in error; //&& "code" in error
}

/**
 * Type guard for Axios errors
 * @param error - Error object to check
 * @returns True if error is an AxiosError
 */
function isAxiosError(error: any): error is AxiosError {
  return !!(error && error.isAxiosError === true);
}

// ==========================================================================
// Helper Functions
// ==========================================================================

/**
 * Gets default error message for HTTP status codes
 * @param status - HTTP status code
 * @returns Default error message
 */
function getDefaultMessage(status: number): string {
  const messages: Record<number, string> = {
    400: "BAD_REQUEST",
    401: "UNAUTHORIZED",
    403: "FORBIDDEN",
    404: "NOT_FOUND",
    500: "INTERNAL_SERVER_ERROR",
  };
  return messages[status] || "UNKNOWN_ERROR";
}

// ==========================================================================
// Translation Utilities
// ==========================================================================

// Translate field errors using provided translation function
export const translateFieldErrors = (
  fieldErrors: FieldErrors,
  t: (key: string, options?: any) => string,
  params?: Record<string, any>,
  namespace = "errors"
): FieldErrors => {
  const translated: FieldErrors = {};

  Object.entries(fieldErrors).forEach(([field, errors]) => {
    translated[field] = errors.map((errorKey) => {
      // For field-specific errors, we include the field name in params
      const fieldParams = { ...params, field };
      return t(`${namespace}:${errorKey}`, fieldParams);
    });
  });

  return translated;
};

// Utility to extract first global error with translation
export const getFirstTranslatedError = (error: ApiError, t: (key: string, options?: any) => string, namespace = "errors"): string => {
  if (error.globalErrors.length > 0) {
    const errorKey = error.globalErrors[0];
    return t(`${namespace}:${errorKey}`, error.params);
  }

  // If no global errors, try to get first field error
  const firstField = Object.keys(error.fieldErrors)[0];
  if (firstField && error.fieldErrors[firstField].length > 0) {
    const errorKey = error.fieldErrors[firstField][0];
    return t(`${namespace}:${errorKey}`, { ...error.params, field: firstField });
  }

  return t(`${namespace}:UNKNOWN_ERROR`);
};

export const translateGlobalErrors = (
  globalErrors: string[],
  t: (key: string, options?: any) => string,
  params?: Record<string, any>,
  namespace = "errors"
): string[] => {
  return globalErrors.map((errorKey) => t(`${namespace}:${errorKey}`, params));
};
